import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

/**
 * Async Thunks for AppSetting API Calls
 */

// Fetch app settings with filters and pagination
export const getAppSettings = createAsyncThunk(
  'appSettings/getAppSettings',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/app-settings', { params });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Fetch single app setting by ID
export const getAppSettingById = createAsyncThunk(
  'appSettings/getAppSettingById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/app-settings/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Create new app setting
export const createAppSetting = createAsyncThunk(
  'appSettings/createAppSetting',
  async (settingData, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/app-settings', settingData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Update app setting
export const updateAppSetting = createAsyncThunk(
  'appSettings/updateAppSetting',
  async ({ id, settingData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/admin/app-settings/${id}`, settingData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Delete app setting
export const deleteAppSetting = createAsyncThunk(
  'appSettings/deleteAppSetting',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/admin/app-settings/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Get app setting value by key (public endpoint for frontend use)
export const getSettingValue = createAsyncThunk(
  'appSettings/getSettingValue',
  async (key, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/app-settings/value/${key}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * AppSetting Slice
 */
const appSettingSlice = createSlice({
  name: 'appSettings',
  initialState: {
    settings: [],
    currentSetting: null,
    settingValues: {}, // Cache for setting values (key -> value)
    loading: false,
    error: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0
    }
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentSetting: (state) => {
      state.currentSetting = null;
    }
  },
  extraReducers: (builder) => {
    // getAppSettings
    builder
      .addCase(getAppSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAppSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload.settings;
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages
        };
      })
      .addCase(getAppSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Sistem ayarları yüklenemedi';
      });

    // getAppSettingById
    builder
      .addCase(getAppSettingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAppSettingById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSetting = action.payload;
      })
      .addCase(getAppSettingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Sistem ayarı yüklenemedi';
      });

    // createAppSetting
    builder
      .addCase(createAppSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAppSetting.fulfilled, (state, action) => {
        state.loading = false;
        state.settings.unshift(action.payload);
        // Update cache when new setting is created
        if (action.payload?.key) {
          const cacheKey = action.payload.key.toLowerCase();
          state.settingValues[cacheKey] = action.payload.value;
        }
      })
      .addCase(createAppSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Sistem ayarı oluşturulamadı';
      });

    // updateAppSetting
    builder
      .addCase(updateAppSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAppSetting.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.settings.findIndex(
          setting => setting._id === action.payload._id
        );
        if (index !== -1) {
          state.settings[index] = action.payload;
        }
        if (state.currentSetting && state.currentSetting._id === action.payload._id) {
          state.currentSetting = action.payload;
        }
        // Update cache when setting is updated
        if (action.payload?.key) {
          const cacheKey = action.payload.key.toLowerCase();
          state.settingValues[cacheKey] = action.payload.value;
        }
      })
      .addCase(updateAppSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Sistem ayarı güncellenemedi';
      });

    // deleteAppSetting
    builder
      .addCase(deleteAppSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAppSetting.fulfilled, (state, action) => {
        state.loading = false;
        const deletedSetting = state.settings.find(
          setting => setting._id === action.payload
        );
        state.settings = state.settings.filter(
          setting => setting._id !== action.payload
        );
        if (state.currentSetting && state.currentSetting._id === action.payload) {
          state.currentSetting = null;
        }
        // Remove from cache when setting is deleted
        if (deletedSetting?.key) {
          const cacheKey = deletedSetting.key.toLowerCase();
          delete state.settingValues[cacheKey];
        }
      })
      .addCase(deleteAppSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Sistem ayarı silinemedi';
      });

    // getSettingValue
    builder
      .addCase(getSettingValue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSettingValue.fulfilled, (state, action) => {
        state.loading = false;
        // Cache the setting value
        // Backend returns key in lowercase, but we should use the original key format
        if (action.payload?.key) {
          // Use lowercase key to match backend behavior
          const cacheKey = action.payload.key.toLowerCase();
          state.settingValues[cacheKey] = action.payload.value;
        }
      })
      .addCase(getSettingValue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Sistem ayarı değeri alınamadı';
      });
  }
});

export const { clearError, clearCurrentSetting } = appSettingSlice.actions;
export default appSettingSlice.reducer;

